import { AxiosRequestConfig, AxiosResponse } from 'axios';

import {
  I2FAResponse,
  IAutoLoginRequest,
  IAutoLoginResponse,
  ICheckOtpRequest,
  ILoginResponse,
  ILoginRequest,
  IRefreshTokenRequest,
  IRefreshTokenResponse,
  IResetPassword,
  ISendOtpRequest,
  ISignup,
  ISwitchSiteRequest,
  ISwitchViewResponse,
  IWhoAmIResponse,
  IGetThemeByDomainResponse,
} from 'models/http/auth';
import { IValidateSignupResponse } from 'models/response';
import { TwoFactorAuthMethods } from 'utils/enums/auth';
import { HttpTimeoutPriority } from 'utils/enums/http-timeout-priority';
import { serverAxios } from 'utils/http';

class AuthHttp {
  private static route = '/auth';

  public async login(email: string, password: string, deviceToken?: string): Promise<ILoginResponse<boolean>> {
    const body: ILoginRequest = { email: email, password: password, userType: 'moderator' };

    /** If provided, attaching device token to the request */
    deviceToken && (body.deviceToken = deviceToken);

    return serverAxios
      .post(`${AuthHttp.route}/login`, body, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<ILoginResponse<boolean>>) => response.data);
  }

  public async sendOtp(deviceToken: string, authMethod: TwoFactorAuthMethods): Promise<void> {
    const body: ISendOtpRequest = { deviceToken, authMethod, userType: 'moderator' };

    return serverAxios
      .post(`${AuthHttp.route}/otp/send`, body, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<void>) => response.data);
  }

  public async checkOtp(deviceToken: string, otp: string): Promise<ILoginResponse<boolean>> {
    const body: ICheckOtpRequest = { deviceToken, otp, userType: 'moderator' };

    return serverAxios
      .post(`${AuthHttp.route}/otp/check`, body, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<I2FAResponse>) => response.data);
  }

  public async refreshToken(refreshToken: string, options?: { siteId: number; companyId: number }): Promise<IRefreshTokenResponse> {
    const body: IRefreshTokenRequest = { refreshToken, userType: 'moderator', options };

    return serverAxios
      .post(`${AuthHttp.route}/moderator/refresh-token`, body, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<IRefreshTokenResponse>) => response.data);
  }

  public async autoLogin(
    refreshToken: string,
    siteId?: string,
    companyId?: string,
    deviceToken?: string,
  ): Promise<IAutoLoginResponse<boolean>> {
    const view = !isNaN(Number(companyId)) && !isNaN(Number(siteId)) ? { siteId: Number(siteId), companyId: Number(companyId) } : undefined;

    const body: IAutoLoginRequest = { refreshToken, deviceToken, view, userType: 'moderator' };

    return serverAxios
      .post(`${AuthHttp.route}/moderator/auto-login`, body, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<IAutoLoginResponse<boolean>>) => {
        return response.data;
      });
  }

  public async switchView(companyId: number, siteId: number, refreshToken: string): Promise<ISwitchViewResponse> {
    const body: ISwitchSiteRequest = { refreshToken, siteId, companyId };

    return serverAxios
      .post(`${AuthHttp.route}/moderator/switch-view`, body, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<ISwitchViewResponse>) => response.data);
  }

  public async validateSignup(token: string): Promise<IValidateSignupResponse> {
    return serverAxios
      .get(`${AuthHttp.route}/validate-signup/${token}`, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<IValidateSignupResponse>) => response.data);
  }

  public async signup(signupToken: string, data: ISignup): Promise<void> {
    return serverAxios
      .post(`${AuthHttp.route}/signup/${signupToken}`, { ...data, userType: 'moderator' }, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<void>) => response.data);
  }

  public async whoAmI(abortController?: AbortController, accessToken?: string): Promise<IWhoAmIResponse> {
    const config: AxiosRequestConfig = { signal: abortController?.signal, timeout: HttpTimeoutPriority.Highest };

    /** When logging-in directly to profile route, tokenInterceptor doesn't attach accessToken to the request,
     * even though it is available in the session. Added support to manually pass access token to support
     * early auth calls as this usecase.
     */
    if (accessToken) {
      config.headers = { Authorization: `Bearer ${accessToken}` };
    }

    return serverAxios.get(`${AuthHttp.route}/whoami`, config).then((response: AxiosResponse<IWhoAmIResponse>) => response.data);
  }

  public async resetValidate(token: string): Promise<boolean> {
    const userTypeQueryParam = 'userType=moderator';

    return serverAxios
      .get(`${AuthHttp.route}/reset-password/${token}?${userTypeQueryParam}`, { timeout: HttpTimeoutPriority.Medium })
      .then((res: AxiosResponse<boolean>) => {
        return res.data;
      });
  }

  public async resetRequest(email: string): Promise<boolean> {
    return serverAxios
      .post(`${AuthHttp.route}/reset-password`, { email, userType: 'moderator' }, { timeout: HttpTimeoutPriority.Medium })
      .then((res: AxiosResponse<boolean>) => res.data);
  }

  public async resetPassword(token: string, data: IResetPassword): Promise<boolean> {
    return serverAxios
      .post(`${AuthHttp.route}/reset-password/${token}`, { ...data, userType: 'moderator' }, { timeout: HttpTimeoutPriority.Medium })
      .then((res: AxiosResponse<boolean>) => res.data);
  }

  public async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    return serverAxios
      .post(`${AuthHttp.route}/change-password`, { oldPassword, newPassword }, { timeout: HttpTimeoutPriority.Medium })
      .then(() => {
        return;
      });
  }

  public async getThemeLogosByDomain(domain: string, abortController?: AbortController): Promise<IGetThemeByDomainResponse> {
    return serverAxios
      .get(`${AuthHttp.route}/getThemeLogosByDomain/${domain}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetThemeByDomainResponse>) => {
        return response.data;
      });
  }
}

export default AuthHttp;
